import { gql } from 'graphql-request';
import graphqlClient from 'config/graphql-instance';

export interface TextractBoundingBox {
  Width: number;
  Height: number;
  Left: number;
  Top: number;
}

export interface TextractGeometry {
  BoundingBox: TextractBoundingBox;
}

export interface TextractRelationship {
  Type: string;
  Ids: string[];
}

export interface TextractBlock {
  BlockType: string;
  Id: string;
  Text?: string;
  Confidence?: number;
  Geometry: TextractGeometry;
  Page?: number;
  RowIndex?: number;
  ColumnIndex?: number;
  RowSpan?: number;
  ColumnSpan?: number;
  Relationships?: TextractRelationship[];
}

export interface TextractResponse {
  DocumentMetadata: { Pages: number };
  Blocks: TextractBlock[];
}

export interface DocumentOcrOptions {
  format?: 'jpeg' | 'png';
  passThrough?: boolean;
  tables?: boolean;
}

interface GetDocumentTextResponse {
  getDocumentText: TextractResponse;
}

interface GetDocumentTextVariables {
  document: string;
  features?: string[];
}

const queryGetDocumentText = `
  query Query($document: String!, $features: [AnalyzeDocumentFeature!]) {
    getDocumentText(document: $document, features: $features)
  }
`;

const dataUrlToCompressedBase64 = (
  dataUrl: string,
  format: 'jpeg' | 'png' = 'jpeg',
  quality = 0.5,
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      const encodedDataUrl = canvas.toDataURL(`image/${format}`, quality);
      resolve(encodedDataUrl.split(',')[1]);
    };
    img.onerror = () => reject(new Error('Failed to load image for compression'));
    img.src = dataUrl;
  });
};

/**
 * Direct OCR service: calls GraphQL getDocumentText and returns raw response without parsing.
 * Preserves exact OCR response content as returned by the backend.
 */
export const getDocumentOcr = async (
  imageDataUrl: string,
  options: DocumentOcrOptions = {},
): Promise<TextractResponse> => {
  const { format = 'jpeg', passThrough = false, tables = false } = options;
  const client = graphqlClient();
  const query = gql`${queryGetDocumentText}`;

  const pageBase64 = passThrough
    ? imageDataUrl.split(',')[1]
    : await dataUrlToCompressedBase64(imageDataUrl, format);
  const response = await client.request<GetDocumentTextResponse, GetDocumentTextVariables>(
    query,
    { document: pageBase64, features: tables ? ['TABLES'] : undefined },
  );

  // Return raw OCR response without parsing or transformation
  return response.getDocumentText;
};
